import {
    type ReactElement,
    isValidElement,
    type ReactNode,
    useEffect,
    Children,
    useState,
    useRef,
    type FC,
    useId
} from "react";
import {
    useWindowDimensions,
    Platform,
    View
} from "react-native";
import type ISheetContentProps from "./type";
import type {
    ISheetInitialFocusProps
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    useOverlayAccessibility
} from "../../../../helpers/overlay";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useSheetContext,
    SheetContext
} from "../../context";
import type ISheetHeaderProps from "../header/type";
import {
    SHEET_INITIAL_FOCUS_ATTEMPTS,
    getSheetMaxWidth
} from "../../stylesheet";
import {
    getModalMotion
} from "../../../modal/stylesheet";
import BottomSheet from "../../../bottomSheet";
import SheetFooter from "../footer";
import SheetHeader from "../header";
import Modal from "../../../modal";
import SheetPanel from "../panel";

const getIsPart = (node: ReactNode, part: typeof SheetHeader | typeof SheetFooter): node is ReactElement => isValidElement(node) && node.type === part;

const SheetInitialFocus: FC<ISheetInitialFocusProps> = ({
    initialFocusRef,
    isFocusTitle,
    titleId,
    isOpen
}) => {
    useEffect(() => {
        if(!isOpen) {
            return;
        }

        let attempt = 0;

        let frame = 0;

        const focusInitialElement = () => {
            if(isFocusTitle) {
                const titleElement = Platform.OS === "web" && typeof document !== "undefined" ? document.getElementById(titleId) : null;

                if(titleElement) {
                    titleElement.setAttribute("tabindex", "-1");

                    titleElement.focus({
                        preventScroll: true
                    });

                    return;
                }

                if(Platform.OS !== "web" || attempt >= SHEET_INITIAL_FOCUS_ATTEMPTS) {
                    return;
                }

                attempt += 1;

                frame = requestAnimationFrame(focusInitialElement);

                return;
            }

            const element = initialFocusRef.current;

            if(element?.focus) {
                element.focus();

                return;
            }

            if(attempt >= SHEET_INITIAL_FOCUS_ATTEMPTS) {
                return;
            }

            attempt += 1;

            frame = requestAnimationFrame(focusInitialElement);
        };

        focusInitialElement();

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [
        isOpen
    ]);

    return null;
};

const SheetContent: FC<ISheetContentProps> = ({
    "aria-label": ariaLabel,
    initialFocusRef,
    children,
    style
}) => {
    const sheet = useSheetContext({
        part: "Content"
    });

    const {
        shapeConfigs,
        durations,
        spaces
    } = NCoreUIKitTheme.useContext(sheet?.customTheme);

    const {
        height: windowHeight,
        width: windowWidth
    } = useWindowDimensions();

    const isReduceMotionEnabled = useReducedMotion();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const pointerType = usePointerType();

    const modalId = useId();

    const scrimPressRef = useRef(false);

    const inlineRef = useRef<View>(null);

    const [
        isInlineRendered,
        setIsInlineRendered
    ] = useState(false);

    const isInline = sheet !== undefined && !sheet.isModal && sheet.form === "panel";

    const isInlineActive = isInline && sheet.isOpen;

    const currentMotion = getModalMotion({
        isReduceMotionEnabled,
        durations
    });

    const {
        containerProps
    } = useOverlayAccessibility({
        onDismiss: sheet?.requestClose,
        isActive: isInlineActive,
        containerRef: inlineRef,
        isAutoFocused: false,
        isModal: false
    });

    useEffect(() => {
        if(!isInline || !sheet) {
            return;
        }

        if(sheet.isOpen) {
            setIsInlineRendered(true);

            return;
        }

        if(!isInlineRendered) {
            return;
        }

        const timer = setTimeout(() => {
            setIsInlineRendered(false);

            sheet.onClosed();
        }, Math.max(currentMotion.closeMoveDuration, currentMotion.closeFadeDuration));

        return () => {
            clearTimeout(timer);
        };
    }, [
        sheet?.isOpen,
        isInline
    ]);

    if(!sheet) {
        return null;
    }

    const items = Children.toArray(children);

    const header = items.find((item) => getIsPart(item, SheetHeader));

    const footer = items.find((item) => getIsPart(item, SheetFooter));

    const headerProps = isValidElement<ISheetHeaderProps>(header) ? header.props : undefined;

    const labelledBy = headerProps ? sheet.titleId : undefined;

    const describedBy = headerProps?.description ? sheet.descriptionId : undefined;

    const maxWidth = getSheetMaxWidth({
        width: sheet.width,
        size: sheet.size,
        shapeConfigs
    });

    const onScrimTouch = () => {
        scrimPressRef.current = true;
    };

    const overlayProps = {
        onPointerDown: onScrimTouch,
        onTouchStart: onScrimTouch
    };

    const handleOverlayPress = () => {
        const isScrimPress = scrimPressRef.current;

        scrimPressRef.current = false;

        if(isScrimPress && !sheet.isCloseOnOverlay) {
            return;
        }

        sheet.requestClose();
    };

    const handleBottomOpenChange = (isOpen: boolean) => {
        if(!isOpen && sheet.isOpen) {
            sheet.requestClose();
        }
    };

    const renderWithSheet = (node: ReactNode) => {
        return <SheetContext.Provider
            value={sheet}
        >
            {node}
        </SheetContext.Provider>;
    };

    const renderInitialFocus = () => {
        if(!initialFocusRef) {
            return null;
        }

        return <SheetInitialFocus
            isFocusTitle={pointerType === "coarse" || sheet.form === "bottom"}
            initialFocusRef={initialFocusRef}
            titleId={sheet.titleId}
            isOpen={sheet.isOpen}
        />;
    };

    const renderBottom = () => {
        const body = items.filter((item) => item !== header && item !== footer);

        return <BottomSheet
            snapPoint={sheet.edge === "start" || sheet.edge === "end" ? windowHeight : undefined}
            renderBottom={footer ? () => renderWithSheet(footer) : undefined}
            renderHeader={header ? () => renderWithSheet(header) : undefined}
            onOverlayPressed={handleOverlayPress}
            customLocalize={sheet.customLocalize}
            onOpenChange={handleBottomOpenChange}
            modalProps={{
                "aria-describedby": describedBy,
                "aria-labelledby": labelledBy,
                "aria-label": ariaLabel,
                overlayProps
            }}
            customTheme={sheet.customTheme}
            isSwipeClose={!sheet.isDirty}
            isWorkWithAnimation={true}
            onClosed={sheet.onClosed}
            isCloseOnOverlay={false}
            isOpen={sheet.isOpen}
            isShowClose={false}
            style={style}
        >
            {renderWithSheet(body)}
            {renderInitialFocus()}
        </BottomSheet>;
    };

    const renderPanel = (isFadeEnabled: boolean) => {
        return renderWithSheet(<SheetPanel
            width={Math.min(windowWidth * shapeConfigs.sheetWidthRatio, maxWidth)}
            aria-describedby={isInline ? describedBy : undefined}
            aria-labelledby={isInline ? labelledBy : undefined}
            role={isInline ? "complementary" : undefined}
            aria-label={isInline ? ariaLabel : undefined}
            maxHeight={windowHeight - spaces.spacingXxl}
            isFadeEnabled={isFadeEnabled}
            direction={direction}
            id={sheet.contentId}
            style={style}
        >
            {children}
            {renderInitialFocus()}
        </SheetPanel>);
    };

    if(sheet.form === "bottom") {
        return renderBottom();
    }

    if(isInline) {
        if(!isInlineRendered && !sheet.isOpen) {
            return null;
        }

        return <View
            {...containerProps}
            style={stylesheet.inlineLayer}
            ref={inlineRef}
        >
            {renderPanel(isReduceMotionEnabled)}
        </View>;
    }

    return <Modal
        customLocalize={sheet.customLocalize}
        onOverlayPress={handleOverlayPress}
        customTheme={sheet.customTheme}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        overlayProps={overlayProps}
        onClosed={sheet.onClosed}
        aria-label={ariaLabel}
        isOpen={sheet.isOpen}
        alignContent="free"
        role="dialog"
        id={modalId}
    >
        {renderPanel(false)}
    </Modal>;
};
export default SheetContent;
